/**
 * @file billLineFields.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-16
 * @modified 2026-10-28
 * @description Add Bill, field by field: a bill line's own value against the
 *              PO line it bills (Cam, 16 Oct 2026 — "just clicking a field can
 *              offer PO's value, Bill's value, or choose / input another …
 *              POs and bills might not always match exactly but the user will
 *              know what they are doing so let them, just save them typing").
 *
 *   For each cell of a bill line on a PO line — item, description, quantity,
 *   unit price, account, tax — the PO's value and the bill's (what the
 *   invoice said, kept on the line as `_bill` when it was matched to the PO
 *   line), which one is in use, and whether they differ. Nothing here
 *   refuses anything: every cell stays typeable.
 *
 *   DEFAULTS (set where a bill line meets its PO line — utils/billLineReconcile
 *   and Add Bill; Cam, 16 Oct 2026): from the PO line — item, description
 *   (the purchase words), account and tax; from the BILL — quantity and unit
 *   price. The PO's quantity (what was supplied, else what was ordered) is
 *   the alternative in the cell, and a difference is a note on the line
 *   (and a question when the bill is saved — utils/billQtyOnSave). Header
 *   dates and number the bill's. (1.0.0 started the quantity from what was
 *   supplied; Cam: "to the bill for qty".)
 *
 *   DIRECT DELIVERY (`directDeliveryInPrice`, 1.1.0): a bill line for a
 *   product on a Direct Delivery PO whose price INCLUDES the delivery gets
 *   " (includes direct delivery)" after the PO's words by default (the user
 *   can take it off — the cell offers the PO's words without it). The price
 *   includes it when the bill has no separate haulage line for that PO
 *   (a line on the PO's haulage line, or a charge line — Haul, Carriage…)
 *   and either the PO has no haulage line for the product (no rate entered:
 *   the PO's cost is the delivered price) or the bill's price is nearer the
 *   product's cost + the haulage a unit (the PO haulage line's amount ÷ the
 *   product's quantity) than the cost alone. In the second case the PO
 *   already carries the haulage on its own line, so "Also set the PO's cost"
 *   starts unticked (`haulageInPrice`) — the product's cost would count it
 *   twice.
 *
 *   "ALSO UPDATE THE PO TO THIS" — the in-cell tick that puts the line's value
 *   on the PO line when the bill is saved, through the paths that already
 *   exist (cost: the user's "Update PO from bill" choice, `po_cost_updates`;
 *   quantity and words: `po_edits`). Offered only where the value in use
 *   differs from the PO's; greyed with the reason where the PO cannot take it
 *   from a bill (`poUpdateBlock`): a bill in Xero already; quantity — a PO
 *   made in Xero or whose order is delivered / invoiced (utils/poEditScope),
 *   a split family's shared line, a supplied quantity recorded (ordered is
 *   not changed from a bill), a PO already billed by another bill, several
 *   bill lines on the one PO line, a bill still in another unit; words — a PO
 *   made in Xero (its words live in Xero), several bill lines; item — never
 *   (changed on the PO or its order).
 *
 *   1.2.0 (2026-10-28, Cam — units clear on every price): the Unit Price
 *   card's PO and Bill figures say what they are per — the PO line's unit
 *   ("£10.00/25kg bag"), the bill's printed one ("£400.00/T", "£0.40/kg")
 *   else the PO line's (utils/unitPriceLabel).
 *
 *   Pure.
 */

import { roundHalfUp, formatQuantity } from './numberFormatters';
import { withoutAdditiveNames } from './lineItems';
import { quantityInUnit, money } from './billLineChoices';
import { perUnitOf } from './unitPriceLabel';

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = parseFloat(String(v).replace(/[£,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};
const r4 = (n) => roundHalfUp(n, 4);
const words = (s) => String(s ?? '').replace(/\s+/g, ' ').trim();
const up = (s) => String(s ?? '').trim().toUpperCase();

/** The words a Direct Delivery product line's description takes when its price includes the delivery. */
export const DD_SUFFIX = ' (includes direct delivery)';
/** Words with the suffix (once). */
export const withDdSuffix = (text) => {
  const w = words(text);
  return w.toLowerCase().endsWith(DD_SUFFIX.trim().toLowerCase()) ? w : `${w}${DD_SUFFIX}`;
};
/** Words without it. */
export const withoutDdSuffix = (text) => words(String(text ?? '').replace(/\s*\(includes direct delivery\)\s*$/i, ''));

/**
 * Does this bill line's price include the Direct Delivery haulage?
 *
 * @param {object} p
 * @param {object} p.po — the PO (supplier_delivery_terms, line_items)
 * @param {object} p.li — the product's PO line
 * @param {number|string} p.billPrice — the bill line's unit price (in the PO line's unit)
 * @param {boolean} [p.separateHaulage] — the bill has its own haulage line for this PO
 * @returns {{ includes: boolean, haulageInPrice: boolean, perUnit: number|null, reason: string }}
 */
export function directDeliveryInPrice({ po, li, billPrice, separateHaulage = false } = {}) {
  const no = (reason) => ({ includes: false, haulageInPrice: false, perUnit: null, reason });
  if (String(po?.supplier_delivery_terms || '') !== 'Direct Delivery') return no('Not a Direct Delivery PO');
  if (!li || li.kind === 'haulage') return no('A haulage line');
  if (separateHaulage) return no('The bill has its own haulage line');
  const h = (Array.isArray(po.line_items) ? po.line_items : []).find(x => x?.kind === 'haulage'
    && li.orderLineId != null && String(x.haulageForOrderLineId) === String(li.orderLineId));
  const qty = num(li.quantity);
  const amount = h ? (num(h.lineAmount) ?? ((num(h.quantity) || 0) * (num(h.unitAmount) || 0))) : null;
  const perUnit = h && qty > 0 && amount > 0 ? r4(amount / qty) : null;
  if (!perUnit) return { includes: true, haulageInPrice: false, perUnit: null, reason: 'Direct Delivery with no haulage line on the PO — its cost is the delivered price' };
  const cost = num(li.unitAmount) || 0;
  const price = num(billPrice);
  if (price === null) return no('No price on the bill line');
  const includes = Math.abs(price - (cost + perUnit)) < Math.abs(price - cost);
  return includes
    ? { includes: true, haulageInPrice: true, perUnit, reason: `The bill's price is the product's cost + £${perUnit} a unit of haulage` }
    : no("The bill's price is the product's cost alone");
}

/** Do two values of a field agree? */
export function sameValue(field, a, b) {
  if (field === 'qty' || field === 'price') {
    const x = num(a);
    const y = num(b);
    if (x === null || y === null) return x === y;
    return Math.abs(r4(x) - r4(y)) < 0.00005;
  }
  if (field === 'desc') return words(a).toLowerCase() === words(b).toLowerCase();
  return up(a) === up(b);
}

/** A PO line's unit word for quantityInUnit: 't' (1000 kg), 'pack', or none. */
export function poUnitWordOf(kg) {
  const k = num(kg);
  if (!(k > 0)) return null;
  return k === 1000 ? 't' : 'pack';
}

/**
 * Why the PO line cannot take this field's value from the bill, or null.
 * @param {'qty'|'desc'|'price'|'item'} field
 * @param {object} p
 * @param {boolean} p.editable — may this bill change its POs at all
 * @param {object|null} p.scope — utils/poEditScope (null: anything may change)
 * @param {boolean} [p.splitFamily]
 * @param {number|null} [p.supplied] — supplied quantity recorded on the PO line
 * @param {boolean} [p.billedElsewhere]
 * @param {boolean} [p.several] — several bill lines bill this PO line
 * @param {boolean} [p.unconverted] — the bill is still in another unit than the PO line
 * @param {string} [p.poNumber]
 */
export function poUpdateBlock(field, p = {}) {
  const no = p.poNumber || 'The PO';
  if (field === 'item') return "A PO line's item is changed on the PO or its customer order, not from a bill.";
  if (p.editable === false) return 'This bill is in Xero already — its POs are not changed from it.';
  if (field === 'price') return null;
  if (p.several) return 'Several bill lines bill this PO line — change the PO itself.';
  if (field === 'desc') {
    if (p.scope && p.scope.mode !== 'delivered') return p.scope.reason || "Its words live in Xero.";
    return null;
  }
  if (field === 'qty') {
    if (p.scope) return p.scope.reason || "Only this PO's cost changes from a bill.";
    if (p.splitFamily) return "This PO line is shared by split orders — change each order's quantity on the order itself.";
    if (p.supplied !== null && p.supplied !== undefined) return `What was supplied (${formatQuantity(p.supplied)}) is recorded — the PO's ordered quantity is not changed from a bill.`;
    if (p.billedElsewhere) return `${no} is already billed by another bill — its quantity is not changed from this one.`;
    if (p.unconverted) return "The bill is in another unit — put it in the PO's units first.";
  }
  return null;
}

/**
 * Each cell's PO value and bill value for one bill line on a PO line.
 *
 * @param {object} p
 * @param {object} p.line — the grid line (quantity, unit_price, product_name, product_code,
 *        account_code, tax_type, bill_unit, `_bill` — the invoice's values, `_asBilled`)
 * @param {object} p.li — the PO's line_items element
 * @param {number|null} [p.supplied] — supplied quantity recorded (not on a Xero-made PO)
 * @param {object|null} [p.conversion] — utils/billUnitConversion for the bill's own figures
 * @param {boolean} [p.ddSuffix] — the PO's words take " (includes direct delivery)" (directDeliveryInPrice)
 * @returns {Object<'item'|'desc'|'qty'|'price'|'account'|'tax', { po, bill, active, differs }>}
 *          po / bill: { value, text } | null; active: 'po' | 'bill' | null (own) — 'po' when both agree
 */
export function lineFieldSides({ line, li, supplied = null, conversion = null, ddSuffix = false } = {}) {
  const out = {};
  const bill = line?._bill || null;
  const kg = num(li?.unitWeightKg);
  const ordered = num(li?.quantity) ?? 0;
  const poQty = supplied !== null && supplied !== undefined ? num(supplied) : ordered;
  // What each price is per: the PO line's unit; the bill's printed one, else the PO line's.
  const poPer = perUnitOf({ unitWeightKg: li?.unitWeightKg, itemUnitWeightKg: li?.itemUnitWeightKg }).per;
  const billUnit = String(bill?.bill_unit || line?.bill_unit || '').toLowerCase();
  const billPer = billUnit === 't' || billUnit === 'kg' ? perUnitOf({ unit: billUnit }).per : poPer;
  const sides = {
    item: {
      po: up(li?.itemCode) ? { value: up(li.itemCode), text: String(li.itemCode).trim() } : null,
      bill: bill && up(bill.product_code) ? { value: up(bill.product_code), text: String(bill.product_code).trim() } : null,
      current: line?.product_code,
    },
    desc: {
      po: words(li?.description) ? (() => {
        const w = words(withoutAdditiveNames(li.description, li.additives));
        const v = ddSuffix ? withDdSuffix(w) : w;
        return { value: v, text: v, ...(ddSuffix ? { plain: w } : {}) };
      })() : null,
      bill: bill && words(bill.product_name) ? { value: words(bill.product_name), text: words(bill.product_name) } : null,
      current: line?.product_name,
    },
    qty: {
      po: { value: poQty, text: supplied !== null && supplied !== undefined
        ? `${quantityInUnit(poQty, poUnitWordOf(kg), kg)} supplied (${quantityInUnit(ordered, poUnitWordOf(kg), kg)} ordered)`
        : `${quantityInUnit(ordered, poUnitWordOf(kg), kg)} ordered` },
      bill: bill && num(bill.quantity) !== null ? { value: num(bill.quantity), text: quantityInUnit(num(bill.quantity), bill.bill_unit || null) } : null,
      current: line?.quantity,
    },
    price: {
      po: { value: num(li?.unitAmount) ?? 0, text: num(li?.unitAmount) > 0 ? `${money(li.unitAmount)}${poPer}` : 'None (£0)' },
      bill: bill && num(bill.unit_price) !== null ? { value: num(bill.unit_price), text: `${money(bill.unit_price)}${billPer}` } : null,
      current: line?.unit_price,
    },
    account: {
      po: up(li?.accountCode) ? { value: up(li.accountCode), text: up(li.accountCode) } : null,
      bill: bill && up(bill.account_code) ? { value: up(bill.account_code), text: up(bill.account_code) } : null,
      current: line?.account_code,
    },
    tax: {
      po: up(li?.taxType) ? { value: up(li.taxType), text: up(li.taxType) } : null,
      bill: bill && up(bill.tax_type) ? { value: up(bill.tax_type), text: up(bill.tax_type) } : null,
      current: line?.tax_type,
    },
  };
  for (const [field, s] of Object.entries(sides)) {
    const differs = !!(s.po && s.bill) && !sameValue(field, s.po.value, s.bill.value);
    let active = null;
    if (s.po && sameValue(field, s.current, s.po.value)) active = 'po';
    else if (s.bill && sameValue(field, s.current, s.bill.value)) active = 'bill';
    out[field] = { po: s.po, bill: s.bill, active, differs };
  }
  // Billed in another unit: the quantity and price are a pair — the bill's
  // units or the PO's (Lacto: per kg against 25kg bags).
  if (conversion || line?._asBilled) {
    out.qty.units = true;
    out.qty.differs = true;
    out.qty.active = line?._asBilled ? 'po' : 'bill';
    out.price.units = true;
    out.price.differs = !!(out.price.po && out.price.po.value > 0) || !!line?._asBilled || !!conversion;
  }
  return out;
}

/**
 * The quantity a bill line starts with where it meets its PO line: the
 * BILL's own (Cam, 16 Oct 2026). The PO's — what was supplied, else what was
 * ordered — is the alternative, and whether they differ.
 * @returns {{ quantity: number|null, from: 'bill', alternative: number|null, alternativeFrom: 'supplied'|'ordered', differs: boolean }}
 */
export function startingQuantity({ billQty, supplied = null, ordered = null }) {
  const b = num(billQty);
  const s = num(supplied);
  const alt = s !== null ? s : num(ordered);
  return {
    quantity: b, from: 'bill', alternative: alt, alternativeFrom: s !== null ? 'supplied' : 'ordered',
    differs: alt !== null && b !== null && !sameValue('qty', b, alt),
  };
}

/** "Bill 2,000.00 kg at £2.015/kg = 80 × 25kg bag at £50.375/bag (same £4,030.00)". */
export function unitsMathsText(take, conv) {
  if (!take?.ok) return '';
  const per = (unit) => {
    if (unit === 'kg') return 'kg';
    if (unit === 't') return 'T';
    const lbl = conv?.kg ? quantityInUnit(1, 'pack', conv.kg).replace(/^1 × /, '') : 'unit';
    return lbl.split(' ').pop() || 'unit';
  };
  const from = quantityInUnit(take.asBilled.quantity, take.asBilled.unit, conv?.kg);
  const to = quantityInUnit(take.quantity, take.unit, conv?.kg);
  const pounds = `£${(Math.round(take.amount * 100) / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `Bill ${from} at ${money(take.asBilled.unit_amount)}/${per(take.asBilled.unit)} = ${to} at ${money(take.unitPrice)}/${per(take.unit)} (same ${pounds})`;
}

/**
 * What the save's audit row records of the choices (`line_choices`, as
 * utils/billLineChoices.lineChoicesForAudit takes it): per PO line, each field
 * taken from the PO ('po'), put on the PO from the bill ('bill'), or a cost
 * kept different ('keep'); 'units: po' for a line in the PO's units.
 *
 * @param {Array<{ purchase_order_id, po_line_key, purchase_order_number?, description?, choices }>} rows
 */
export function lineChoicesFor(rows) {
  return (rows || []).filter(r => r && r.choices && Object.keys(r.choices).length).map(r => ({
    purchase_order_id: Number(r.purchase_order_id),
    po_line_key: String(r.po_line_key),
    ...(r.purchase_order_number ? { purchase_order_number: r.purchase_order_number } : {}),
    ...(r.description ? { description: String(r.description).slice(0, 200) } : {}),
    choices: r.choices,
  }));
}
